import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Container, Form, FloatingLabel } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import useAxios from '../../services/AxiosConfig';
import styles from '../ManagementPages.module.css';
import {  TrashIcon, AddQuestionIcon } from '../../components/icons/Icons';
import { showAlert } from '../../components/PerinHealthAlertSlice';

const AddTablet = () => {

    const groupData = useSelector((state) => state.userInfo.groups);
    const uniqueGroups = useSelector((state) => state.userInfo.uniqueGroups);
    const isAdmin = useSelector((state) => state.userInfo.isAdmin);

    const dispatch = useDispatch();
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const httpService = useAxios(axiosConfig.envURL);

    const tabletSlice = useSelector((state)=>state.tabletManagement);
    const editTabletData = tabletSlice.data;
    const updatingTablet = tabletSlice.updating;

    const [errorMessage, setErrorMessage] = useState(null);
    const formRef = useRef(null);
    const [validated, setValidated] = useState(false);

    const [quantity, setQuantity] = useState(Number(editTabletData.quantity) || '');

    const [tabletEntries, setTabletEntries] = useState(
        updatingTablet 
            ? editTabletData.tablets.map(num => ({
                serialNumber: num.serialNumber,
                macAddress: num.macAddress || ''
              }))
            : Array(editTabletData.quantity || 0).fill({ serialNumber: '', macAddress: '' })
    );

    const assignedSerialNumbers = Array.from(new Set((editTabletData.tablets || []).filter((item)=>item.isAssigned).map((item)=>item.serialNumber)));

    // State to track duplicate entries
    const [duplicateSerialNumbers, setDuplicateSerialNumbers] = useState(new Set());
    const [duplicateMacAddresses, setDuplicateMacAddresses] = useState(new Set());

    const [selectedGroup, setSelectedGroup] = useState(updatingTablet ? editTabletData.groupId : (uniqueGroups.length ===1 ) ? uniqueGroups[0].id : 0);

    const [selectedOrg, setSelectedOrg] = useState(updatingTablet ? editTabletData.organizationId : 0);

    const navigate = useNavigate();

    const handleGroupChange = (e) => {
        setSelectedGroup(Number(e.target.value));
    };

    const handleQuantityChange = (e) => {
        const value = e.target.value;
        const numValue = Number(value);
        if (Number.isNaN(numValue) || numValue <= 0) {
            return;
        }
        
        // Count all entries with serial numbers (saved ones + newly entered ones)
        // This includes both the original saved serial numbers and any new ones the user has entered
        const entriesWithSerialNumbers = tabletEntries.filter(entry => entry.serialNumber.trim() !== '').length;
        
        // The minimum quantity is the number of entries that have serial numbers entered
        // This accounts for: saved serial numbers + any newly entered serial numbers
        const minQuantity = entriesWithSerialNumbers;
        
        // Prevent reducing quantity below the number of entries with serial numbers
        if (numValue < minQuantity) {
            // Don't allow the change - keep current quantity
            return;
        }
        
        setQuantity(numValue);
        // Update tabletEntries array to match new quantity
        const currentLength = tabletEntries.length;
        if (numValue > currentLength) {
            // Add empty entries if quantity increased
            const newEntries = [...tabletEntries];
            for (let i = currentLength; i < numValue; i++) {
                newEntries.push({ serialNumber: '', macAddress: '' });
            }
            setTabletEntries(newEntries);
        } else if (numValue < currentLength) {
            // Remove entries if quantity decreased
            setTabletEntries(tabletEntries.slice(0, numValue));
        }
    };

    const handleSerialNumberChange = (index, value) => {
        const updatedEntries = [...tabletEntries];
        updatedEntries[index] = {
            ...updatedEntries[index],
            serialNumber: value
        };
        setTabletEntries(updatedEntries);
        
        // Clear duplicate highlighting when user makes changes
        if (duplicateSerialNumbers.has(index)) {
            const newDuplicates = new Set(duplicateSerialNumbers);
            newDuplicates.delete(index);
            setDuplicateSerialNumbers(newDuplicates);
        }
        // Clear error message if it was about duplicates
        if (errorMessage === 'Please ensure all serial numbers and mac addresses are unique.') {
            setErrorMessage(null);
        }
    };

    const handleMacAddressChange = (index, value) => {
        const updatedEntries = [...tabletEntries];
        updatedEntries[index] = {
            ...updatedEntries[index],
            macAddress: value
        };
        setTabletEntries(updatedEntries);
        
        // Clear duplicate highlighting when user makes changes
        if (duplicateMacAddresses.has(index)) {
            const newDuplicates = new Set(duplicateMacAddresses);
            newDuplicates.delete(index);
            setDuplicateMacAddresses(newDuplicates);
        }
        // Clear error message if it was about duplicates
        if (errorMessage === 'Please ensure all serial numbers and mac addresses are unique.') {
            setErrorMessage(null);
        }
    };

    const handleSerialDelete = (index) => {
        const updatedEntries = tabletEntries.filter((_, i) => i !== index);
        setTabletEntries(updatedEntries);
        setQuantity(updatedEntries.length);
    };

    const addSerialNumber = () => {
        const updatedEntries = [...tabletEntries, { serialNumber: '', macAddress: '' }];
        setTabletEntries(updatedEntries);
        setQuantity(updatedEntries.length);
    };

    const findDuplicates = (entries) => {
        const serialNumberCounts = {};
        const macAddressCounts = {};
        const duplicateSerials = new Set();
        const duplicateMacs = new Set();

        // Count occurrences
        entries.forEach((entry, index) => {
            if (entry.serialNumber.trim()) {
                serialNumberCounts[entry.serialNumber] = (serialNumberCounts[entry.serialNumber] || []).concat(index);
            }
            if (entry.macAddress.trim()) {
                macAddressCounts[entry.macAddress] = (macAddressCounts[entry.macAddress] || []).concat(index);
            }
        });

        // Find duplicates
        Object.entries(serialNumberCounts).forEach(([serial, indices]) => {
            if (indices.length > 1) {
                indices.forEach(index => duplicateSerials.add(index));
            }
        });

        Object.entries(macAddressCounts).forEach(([mac, indices]) => {
            if (indices.length > 1) {
                indices.forEach(index => duplicateMacs.add(index));
            }
        });

        return { duplicateSerials, duplicateMacs };
    };

    const validateTablet = () => {
        // Clear previous duplicate highlighting
        setDuplicateSerialNumbers(new Set());
        setDuplicateMacAddresses(new Set());

        if (formRef.current) {
            let formIsInvalid = formRef.current.checkValidity() === false;
            setValidated(true); 
            if (formIsInvalid) {
                setErrorMessage('Group and Quantity are required to proceed');
                return;
            }
        }
        if (!quantity || quantity <= 0) {
            setErrorMessage('Group and Quantity are required to proceed');
            return;
        }
        let errorMessage = null;
        if (quantity > 0 && selectedGroup !== 0 && selectedGroup !== ""){
            const entriesToCheck = tabletEntries.slice(0, quantity);
            const allEntriesFilled = entriesToCheck.every((entry) => 
                entry.serialNumber.trim() !== "" &&  entry.macAddress.trim() !== ""
            );
            if (allEntriesFilled && entriesToCheck.length === quantity) {
                const { duplicateSerials, duplicateMacs } = findDuplicates(entriesToCheck);
                
                if (duplicateSerials.size === 0 && duplicateMacs.size === 0) {
                    if (updatingTablet) {
                        updateTablet()
                    } else {
                        sendTablet();
                    }
                } else {
                    // Set duplicate highlighting
                    setDuplicateSerialNumbers(duplicateSerials);
                    setDuplicateMacAddresses(duplicateMacs);
                    errorMessage = 'Please ensure all serial numbers and mac addresses are unique.'
                }
            } else {
                errorMessage = 'Please fill in all serial numbers and mac addresses.';
            }
        }
        setErrorMessage(errorMessage);
    };

    const sendTablet = async () => {
        const entriesToSend = tabletEntries.slice(0, quantity);
        const tabletInfo = {
            "groupId": selectedGroup,
            "serialNumbers": entriesToSend
        };
        try {
            const response = await httpService.request({
                method: 'POST',
                headers: {"Content-Type": "application/json"},
                url: 'tablet',
                data: JSON.stringify(tabletInfo),
            });
            if (response) {
                dispatch(
                    showAlert({
                        header: "Success",
                        message: `Added tablet successfully`,
                        type: 'success'
                    })
                );
                navigate('/tablet-management')
            }
        } catch (error) {
            console.log("error:"+error);
            dispatch(
                showAlert({
                    header: "Error",
                    message: "Could not add tablet",
                    type: 'danger',
                })
            );
        }
    };

    const updateTablet = async () => {
        if (quantity === "" || quantity <= 0) {
            alert("Please enter a valid quantity");
        } else {
            const entriesToSend = tabletEntries.slice(0, quantity);
            const info = { 
                serialNumbers: entriesToSend
            };
            try {
                const response = await httpService.request({
                    method: 'PUT',
                    headers: { "Content-Type": "application/json" },
                    url: `tablet/${editTabletData.groupId}`,
                    data: JSON.stringify(info),
                });
                if (response) {
                    dispatch(
                        showAlert({
                            header: "Success",
                            message: "Tablet updated successfully",
                            type: 'success'
                        })
                    );
                    navigate('/tablet-management');
                }
            } catch (error) {
                console.log("error:" + error);
                dispatch(
                    showAlert({
                        header: "Error",
                        message: "Error updating tablet",
                        type: 'danger',
                        dismissable: true
                    })
                );
            }
        }
    };

    return (
        <div className={styles.MainCard}>
            <div className={styles.ContentTitle}>{updatingTablet ? "Update Tablet" : "Add Tablet"}</div>
            <div className={styles.HideScrollContainer}>
                <Form ref={formRef} noValidate validated={validated}>
                {isAdmin &&
                    <Form.Group className="mb-3">
                        <FloatingLabel controlId="floatingSelect" label="Organization">
                        <Form.Select
                            disabled={updatingTablet}
                            value={selectedOrg}
                            style={{width:"300px"}}
                            onChange={(e)=>setSelectedOrg(Number(e.target.value))}
                            className={`${styles.BorderedComponent}`}
                            required
                        >
                            <option value={''}>Select an Organization</option>
                            {groupData.map((org)=> (
                                <option key={org.organizationId} id={org.organizationId} value={org.organizationId}>{org.organizationName}</option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            Please select an organization.
                        </Form.Control.Feedback>
                        </FloatingLabel>
                    </Form.Group>
                }
                <Form.Group className="mb-3">
                    <FloatingLabel controlId="tabletGroup" label="Group">
                    <Form.Select
                        className={`${styles.BorderedComponent}`}
                        onChange={handleGroupChange}
                        value={selectedGroup}
                        disabled={updatingTablet}
                        required
                        style={{width: "300px"}}
                    >
                        <option value="">Select a group</option>
                        {isAdmin ? 
                            groupData.filter(organization => organization.organizationId === selectedOrg)
                                .map((organization) => (
                                    organization.groups.map((group) => (
                                        <option key={group.groupId} value={group.groupId}>
                                        {group.groupName}
                                        </option>
                                    ))
                                ))
                            :
                            uniqueGroups.map((group)=> (
                                <option key={group.id} value={group.id}>{group.name}</option>
                            ))
                        }
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                        Please select a group.
                    </Form.Control.Feedback>
                    </FloatingLabel>
                </Form.Group>
                <Container style={{paddingLeft:"0px", marginLeft:"0"}}>
                    <Form.Group className="mb-3">
                        <FloatingLabel 
                            controlId="tabletQuantity"
                            label="Quantity*"
                            className="mb-3"
                        >
                        <Form.Control
                            placeholder="Quantity*"
                            className={`${styles.BorderedComponent}`}
                            style={errorMessage && quantity <= 0 ? {width:"130px", border: "1px solid #DC3545"} : {width:"130px"}}
                            value={quantity}
                            onChange={handleQuantityChange}
                            maxLength={3}
                            isInvalid={errorMessage && quantity <= 0}
                            required
                        />
                        <Form.Control.Feedback type="invalid">
                            Please enter a valid quantity.
                        </Form.Control.Feedback>
                        </FloatingLabel>
                    </Form.Group>
                    <div>
                    {Array.from({ length: quantity || 0 }).map((_, index) => (
                        <div key={`tablet_entry_${index}`} style={{padding: "10px 0", justifyContent: "flex-start", display:"flex", alignItems:"center", gap:"15px"}}>
                            <Form.Group key={`serial_${index}`} >
                                <FloatingLabel controlId={`serialNumber${index}`} label={`Serial Number ${index + 1}`}>
                                    <Form.Control
                                        type="text"
                                        placeholder={`Serial Number ${index + 1}`}
                                        value={tabletEntries[index]?.serialNumber || ''}
                                        onChange={(e) => handleSerialNumberChange(index, e.target.value)}
                                        required
                                        isInvalid={duplicateSerialNumbers.has(index)}
                                        style={duplicateSerialNumbers.has(index) ? {border: "2px solid #DC3545"} : {}}
                                    />
                                    {duplicateSerialNumbers.has(index) && (
                                        <Form.Control.Feedback type="invalid">
                                            This serial number is duplicated.
                                        </Form.Control.Feedback>
                                    )}
                                </FloatingLabel>
                            </Form.Group>
  
                            <Form.Group key={`mac_${index}`} >
                                <FloatingLabel controlId={`macAddress${index}`} label={`Mac Address`}>
                                    <Form.Control
                                        type="text"
                                        required
                                        placeholder={`Mac Address`}
                                        value={tabletEntries[index]?.macAddress || ''}
                                        onChange={(e) => handleMacAddressChange(index, e.target.value)}
                                        isInvalid={duplicateMacAddresses.has(index)}
                                        style={duplicateMacAddresses.has(index) ? {border: "2px solid #DC3545"} : {}}
                                    />
                                    {duplicateMacAddresses.has(index) && (
                                        <Form.Control.Feedback type="invalid">
                                            This MAC address is duplicated.
                                        </Form.Control.Feedback>
                                    )}
                                </FloatingLabel>
                            </Form.Group>
     
                        {!assignedSerialNumbers.includes(tabletEntries[index]?.serialNumber) ?
                            <div onClick={()=>handleSerialDelete(index)} >
                                <TrashIcon />
                            </div>
                            :
                            <div style={{color: "red", fontSize:"13px"}}>
                                {"Active"}
                            </div>
                        }
                 
                    </div>
                    ))}
                </div>
                <Button variant="primary" style={{marginBottom:"30px"}} className={styles.AddAnotherButton} onClick={addSerialNumber}>
                    <AddQuestionIcon /> Add Serial Number
                </Button>
                </Container>
                </Form>
            </div>
            <div className={styles.ButtonsFooterContainerWithError}>
                {errorMessage &&
                    <div className="page-error">
                        <FontAwesomeIcon icon="fa-solid fa-triangle-exclamation" size="sm" style={{ color: "red" }} />
                        {errorMessage}
                    </div>
                }
                <div style={{ display: "flex", columnGap: "10px" }}>
                    <Button variant="primary" className={styles.CreateButton}
                        onClick={validateTablet}>
                        {updatingTablet ? "Update" : "Add"}
                    </Button>
                    <Button variant="secondary" className={styles.CancelButton} onClick={() => navigate('/tablet-management')}>
                        Cancel
                    </Button>
                </div>
            </div>
        </div>
    );

}

export default AddTablet;